import { CalendarType } from '@xeetr/types/enums'
import { FoodBookingModel, ServiceBookingModel } from '@xeetr/types/models'
import { startOfDay, startOfMonth } from 'date-fns'
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz'
import { atom } from 'jotai'
import { atomWithStorage, createJSONStorage } from 'jotai/utils'
import { atomWithSearchParams } from 'jotai-location'

import { tenantTimezoneAtom } from './bookings'

// Calendar fullscreen - session storage only (temporary UI state, not persisted to backend)
export const calendarFullscreenAtom = atomWithStorage(
  'calendar_fullscreen',
  false,
  createJSONStorage(() => sessionStorage)
)

// open state variants for different kinds of moves

type MoveBookingDialogStateTime = {
  open: true
  kind: 'time'
  booking: FoodBookingModel | ServiceBookingModel
  timeDifference: number
  onCancel: () => void
}

type MoveBookingDialogStateClearTables = {
  open: true
  kind: 'clearTables'
  booking: FoodBookingModel | ServiceBookingModel
  onConfirm: () => void
  onCancel: () => void
}

// when a booking changes rooms, all previous table assignments are lost
// this kind gives the user a more descriptive warning
type MoveBookingDialogStateNewRoom = {
  open: true
  kind: 'newRoom'
  booking: FoodBookingModel | ServiceBookingModel
  onConfirm: () => void
  onCancel: () => void
}

// when a booking without any tables is dragged onto a table resource
// we prompt the user before creating that first table assignment
type MoveBookingDialogStateAssignTable = {
  open: true
  kind: 'assignTable'
  booking: FoodBookingModel | ServiceBookingModel
  onConfirm: () => void
  onCancel: () => void
}

// closed state

type MoveBookingDialogStateClosed = { open: false; kind?: undefined; onCancel?: undefined }

export type MoveBookingDialogState =
  | MoveBookingDialogStateTime
  | MoveBookingDialogStateClearTables
  | MoveBookingDialogStateNewRoom
  | MoveBookingDialogStateAssignTable
  | MoveBookingDialogStateClosed

export const moveBookingDialogAtom = atom<MoveBookingDialogState>({ open: false })

export const calendarDateAtom = atom<Date | null>(null)

export const calendarTypeAtom = atom<CalendarType>(CalendarType.WEEK)

export const setCalendarDateAtom = atom(null, (_get, set, date: Date) => {
  set(calendarDateAtom, startOfDay(date))
})

export const calendarDateWithTimezoneAtom = atom((get) => {
  const storedDate = get(calendarDateAtom)
  const tenantTimezone = get(tenantTimezoneAtom)

  if (storedDate) {
    return storedDate
  }

  return startOfDay(toZonedTime(new Date(), tenantTimezone))
})

// Show note view - session storage only (temporary UI state, not persisted to backend)
export const showNoteViewAtom = atomWithStorage(
  'show-note-view',
  false,
  createJSONStorage(() => sessionStorage)
)

const currentMonthUrlAtom = atomWithSearchParams('month', '')
const currentMonthStorageAtom = atomWithStorage(
  'current-month',
  '',
  createJSONStorage(() => sessionStorage)
)

export const currentMonthAtom = atom(
  (get) => {
    const urlValue = get(currentMonthUrlAtom)
    const storageValue = get(currentMonthStorageAtom)
    const tenantTimezone = get(tenantTimezoneAtom)
    const monthStr = urlValue || storageValue

    if (monthStr) {
      // Parse tenant-local date string back to Date object
      const parsed = fromZonedTime(new Date(monthStr + '-01T00:00:00'), tenantTimezone)
      return isNaN(parsed.getTime())
        ? startOfMonth(toZonedTime(new Date(), tenantTimezone))
        : startOfMonth(parsed)
    }

    // No stored value, initialize with tenant timezone
    return startOfMonth(toZonedTime(new Date(), tenantTimezone))
  },
  (_get, set, value: Date) => {
    // Store as tenant-local year-month string (YYYY-MM)
    const tenantTimezone = _get(tenantTimezoneAtom)
    const monthString = formatInTimeZone(value, tenantTimezone, 'yyyy-MM')
    set(currentMonthUrlAtom, monthString)
    set(currentMonthStorageAtom, monthString)
  }
)
